import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const EASE = [0.22, 1, 0.36, 1];

// Line-by-line masked reveal for headings.
export const RevealLines = ({ lines = [], className = "", delay = 0, as: Tag = "h1", "data-testid": testId }) => {
  return (
    <Tag className={className} data-testid={testId}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pt-[0.28em] pb-[0.08em] -mt-[0.28em]">
          <motion.span
            className="block"
            initial={{ y: "108%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.5, ease: EASE, delay: delay + i * 0.08 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

// Generic scroll reveal (fade + 16px up, once)
export const Reveal = ({ children, className = "", delay = 0, y = 16, once = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-40px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.45, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
};

// Staggered container
export const Stagger = ({ children, className = "", gap = 0.08, once = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-40px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={{ show: { transition: { staggerChildren: gap } } }}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = "", y = 16 }) => (
  <motion.div
    className={className}
    variants={{ hidden: { opacity: 0, y }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } } }}
  >
    {children}
  </motion.div>
);

// Word-mask reveal for a single heading (scroll triggered)
export const RevealHeading = ({ text, className = "", as: Tag = "h2", "data-testid": testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <div ref={ref} className="overflow-hidden pt-[0.28em] pb-[0.08em] -mt-[0.28em]">
      <motion.div
        initial={{ y: "108%" }}
        animate={{ y: inView ? "0%" : "108%" }}
        transition={{ duration: 0.5, ease: EASE }}
      >
        <Tag className={className} data-testid={testId}>{text}</Tag>
      </motion.div>
    </div>
  );
};

export { EASE };
